"use client";

import Link from "@/components/LocalizedLink";
import { SofiaConnectLogo } from "./SofiaConnectLogo";
import { useTranslation } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import { SOFIA_CONNECT_ASN } from "@/lib/network-metrics";
import { openConsentPreferences } from "./ConsentPreferenceCenter";

export function Footer() {
  const { t, language } = useTranslation();
  // Privacy is the only Legal page with a BG surface (Bulgarian shell,
  // linked from BG form notices). Other Legal pages remain EN-only.
  const privacyHref = language === "bg" ? "/bg/legal/privacy" : "/legal/privacy";

  const footerLinks = {
    [t("products")]: [
      { label: "IP Transit", href: "/services/ip-transit" },
      { label: t("prod_l2vpn"), href: "/services/ethernet-l2vpn" },
      { label: t("prod_dwdm"), href: "/services/wavelengths-dwdm" },
      { label: t("prod_dark_fiber"), href: "/services/dark-fiber" },
    ],
    [t("network")]: [
      { label: t("footer_peering"), href: "/network" },
      { label: t("footer_network_status"), href: "/status" },
      { label: t("footer_looking_glass"), href: "/looking-glass" },
      { label: t("nav_locations"), href: "/locations" },
      { label: t("footer_naas_configurator"), href: "/#configurator" },
      { label: t("footer_developer_portal"), href: "/developers" },
      { label: t("footer_client_portal"), href: "/portal" },
    ],
    [t("tools")]: [
      { label: t("footer_latency_checker"), href: "/tools/latency" },
      { label: t("footer_roi_calculator"), href: "/tools/roi-calculator" },
      { label: t("footer_network_advisor"), href: "/tools/network-advisor" },
      { label: t("footer_ai_advisor"), href: "/ai-advisor" },
      { label: t("footer_provisioning_demo"), href: "/demos/provisioning" },
    ],
    [t("resources")]: [
      { label: t("nav_about"), href: "/about" },
      { label: t("footer_knowledge_base"), href: "/blog" },
      { label: t("footer_insights"), href: "/insights" },
      { label: t("nav_case_studies"), href: "/case-studies" },
      { label: t("footer_news"), href: "/news" },
      { label: t("resources"), href: "/resources" },
      { label: t("footer_faq"), href: "/#faq" },
      { label: t("contact"), href: "/contact" },
    ],
    [t("legal")]: [
      { label: t("footer_terms"), href: "/legal/terms" },
      { label: t("footer_privacy"), href: privacyHref },
      { label: t("footer_aup"), href: "/legal/aup" },
      { label: t("footer_sla"), href: "/legal/sla" },
    ],
  };

  return (
    // Signal treatment: flat Signal surface with a hairline top border.
    // Uses the theme-aware --signal-surface and --signal-line tokens
    // instead of Tailwind's border-border / bg-surface bridging tokens,
    // so the footer sits on the same colour family as the homepage.
    <footer
      className="border-t"
      style={{
        background: "var(--signal-surface)",
        borderColor: "var(--signal-line)",
      }}
      role="contentinfo"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 pb-24">
        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10">
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              {/*
                Footer column titles are top-level page landmarks (Products,
                Network, Tools, Resources, Legal), not nested subsections of
                whatever h2 came last in <main>. They must be h2 so no page
                jumps h1 -> h3 via the footer. Visual class is unchanged.
              */}
              {/* Signal: keep the uppercase eyebrow, wire it to JetBrains
                  Mono to match the homepage section headers. */}
              <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-4">
                {title}
              </h2>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      onClick={() =>
                        trackEvent("footer_link_click", {
                          section: title,
                          label: link.label,
                          url: link.href,
                        })
                      }
                      className="text-sm text-muted hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                {title === t("legal") && (
                  <li>
                    {/* Cookie preferences opens the consent preference
                        centre. Rendered as a <button> because this is a
                        UI action, not navigation — assistive tech should
                        announce it as such. */}
                    {/* Cookie preferences: legally required, preserved.
                        Signal: 2px focus ring corners (was rounded). */}
                    <button
                      type="button"
                      onClick={openConsentPreferences}
                      className="text-sm text-muted hover:text-foreground transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-[2px]"
                    >
                      {t("footer_cookie_preferences")}
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        {/* Signal: hairline via --signal-line to match the top border. */}
        <div
          className="mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderColor: "var(--signal-line)" }}
        >
          <div className="flex items-center gap-3">
            {/* Signal: brand-mark colour tied to --accent, not the legacy
                --primary-light blue, so it reads as the same identity as
                the homepage logo. Logo fill uses currentColor, so a text
                colour on className resolves the SVG paint. */}
            <SofiaConnectLogo
              className="w-7 h-7 text-[color:var(--accent)]"
              aria-hidden={true}
            />
            <div>
              <p className="text-xs text-muted">
                &copy; {new Date().getFullYear()} Sofia Connect EAD. {t("all_rights_reserved")}
              </p>
              <p className="text-xs text-muted mt-0.5">
                {t("footer_web_design")}
              </p>
              {/* Green Hosted Badge */}
              <div className="relative group inline-flex items-center gap-1.5 mt-1.5">
                {/* Leaf icon */}
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="w-3 h-3 shrink-0"
                  style={{ color: "var(--badge-mint)" }}
                  aria-hidden="true"
                >
                  <path
                    d="M13.5 2.5C13.5 2.5 8 2 4.5 5.5C1.5 8.5 2 13.5 2 13.5C2 13.5 4.5 13 6 11.5L7.5 13"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 13.5C2 13.5 6 10 8 8"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M13.5 2.5C13.5 2.5 13 6.5 10.5 9C8.5 11 6 11.5 6 11.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span
                  className="text-[10px] font-mono"
                  // Theme-aware: var(--badge-mint) resolves to #00d4aa in dark
                  // (10.67:1 on #050505, AAA) and #005f4b in light
                  // (8.20:1 on #ffffff, AAA). See globals.css.
                  style={{ color: "var(--badge-mint)" }}
                >
                  {t("footer_powered_by_vercel")}
                </span>
                {/* Tooltip — same theme-aware mint, opaque bg for contrast. */}
                {/* Signal: 2px corners on the tooltip badge (was rounded). */}
                <span
                  className="absolute bottom-full left-0 mb-1.5 hidden group-hover:block whitespace-nowrap rounded-[2px] px-2 py-1 text-[10px] font-mono pointer-events-none z-10"
                  style={{
                    background: "var(--badge-mint-tooltip-bg)",
                    color: "var(--badge-mint)",
                    border: "1px solid var(--badge-mint)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {t("footer_vercel_tooltip")}
                </span>
              </div>
            </div>
          </div>

          {/* Signal: data (ASN, phone, NOC) in JetBrains Mono with
              tabular-nums so digits align. Phone is the canonical
              `+359 2 420 0300` grouping (26 repo uses); the `/about/`
              outlier is fixed in this same PR. */}
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted font-mono tabular-nums">ASN {SOFIA_CONNECT_ASN}</span>
            <span className="text-xs text-muted" aria-hidden="true">&middot;</span>
            <a
              href="tel:+35924200300"
              className="text-xs text-muted hover:text-foreground transition-colors font-mono tabular-nums"
            >
              +359 2 420 0300
            </a>
            <span className="text-xs text-muted" aria-hidden="true">&middot;</span>
            <span className="text-xs text-muted font-mono tracking-wider">NOC 24/7</span>
            <span className="text-xs text-muted" aria-hidden="true">&middot;</span>
            {/* LinkedIn */}
            <a href="https://www.linkedin.com/company/sofia-connect" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-foreground transition-colors" aria-label="Sofia Connect on LinkedIn">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            {/* Facebook */}
            <a href="https://www.facebook.com/SofiaConnect.EAD" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-foreground transition-colors" aria-label="Sofia Connect on Facebook">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
